import { useState, useEffect } from 'react';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  TicketsIcon,
} from '@/config/icons';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { useSecondarySidebarData } from '@/hooks/useSecondarySidebarData';
import {
  CategoriesSection,
  TagsSection,
  AgentsSection,
  CollapsedSidebar,
} from './sidebar';
import {
  CategoryDialog,
  TagDialog,
  DeleteCategoryDialog,
  DeleteTagDialog,
} from './SidebarDialogs';

export function SecondarySidebar() {
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem('secondary-sidebar-collapsed');
    return saved === 'true';
  });

  // Persist collapsed state
  useEffect(() => {
    localStorage.setItem('secondary-sidebar-collapsed', String(collapsed));
  }, [collapsed]);

  const [categoriesOpen, setCategoriesOpen] = useState(true);
  const [tagsOpen, setTagsOpen] = useState(true);
  const [agentsOpen, setAgentsOpen] = useState(true);

  const {
    // Data
    categories,
    tags,
    agents,
    totalTickets,
    unassignedCount,
    loading,

    // Current filters
    currentCategoryId,
    currentTagId,
    currentAgentId,

    // Category dialog
    showCategoryDialog,
    setShowCategoryDialog,
    editingCategory,
    categoryName,
    setCategoryName,
    categoryColor,
    setCategoryColor,
    savingCategory,
    categoryToDelete,
    setCategoryToDelete,
    openNewCategoryDialog,
    openEditCategoryDialog,
    saveCategory,
    deleteCategory,

    // Tag dialog
    showTagDialog,
    setShowTagDialog,
    editingTag,
    tagName,
    setTagName,
    tagColor,
    setTagColor,
    savingTag,
    tagToDelete,
    setTagToDelete,
    openNewTagDialog,
    openEditTagDialog,
    saveTag,
    deleteTag,

    // Navigation
    handleCategoryClick,
    handleTagClick,
    handleAgentClick,

    // Drag handlers
    handleCategoryDragEnd,
    handleTagDragEnd,
  } = useSecondarySidebarData();

  return (
    <>
      <div
        className={cn(
          "h-full border-r border-border bg-muted/30 transition-all duration-300 flex flex-col",
          collapsed ? "w-12" : "w-56"
        )}
      >
        {/* Toggle button */}
        <div className="p-2 border-b border-border flex justify-end">
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="p-2 rounded-lg hover:bg-muted transition-colors"
            aria-label={collapsed ? "Ouvrir la barre latérale" : "Réduire la barre latérale"}
            aria-expanded={!collapsed}
          >
            {collapsed ? (
              <ChevronRightIcon className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronLeftIcon className="h-4 w-4 text-muted-foreground" />
            )}
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {!collapsed ? (
            <div className="p-3 space-y-4">
              {/* All tickets */}
              <button
                onClick={() => handleCategoryClick(null)}
                className={cn(
                  "w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors",
                  !currentCategoryId && !currentTagId
                    ? "bg-muted text-foreground font-medium"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                )}
              >
                <span className="flex items-center gap-2">
                  <TicketsIcon className="h-4 w-4" />
                  Tous les tickets
                </span>
                <Badge variant="secondary" className="text-xs">
                  {totalTickets}
                </Badge>
              </button>

              {/* Categories/Dossiers section */}
              <CategoriesSection
                open={categoriesOpen}
                onOpenChange={setCategoriesOpen}
                loading={loading}
                categories={categories}
                currentCategoryId={currentCategoryId}
                onCategoryClick={handleCategoryClick}
                onNewCategory={openNewCategoryDialog}
                onEditCategory={openEditCategoryDialog}
                onDeleteCategory={setCategoryToDelete}
                onDragEnd={handleCategoryDragEnd}
              />

              {/* Tags section */}
              <TagsSection
                open={tagsOpen}
                onOpenChange={setTagsOpen}
                loading={loading}
                tags={tags}
                currentTagId={currentTagId}
                onTagClick={handleTagClick}
                onNewTag={openNewTagDialog}
                onEditTag={openEditTagDialog}
                onDeleteTag={setTagToDelete}
                onDragEnd={handleTagDragEnd}
              />

              {/* Agents section */}
              <AgentsSection
                open={agentsOpen}
                onOpenChange={setAgentsOpen}
                loading={loading}
                agents={agents}
                unassignedCount={unassignedCount}
                currentAgentId={currentAgentId}
                onAgentClick={handleAgentClick}
              />
            </div>
          ) : (
            <CollapsedSidebar
              totalTickets={totalTickets}
              currentCategoryId={currentCategoryId}
              currentTagId={currentTagId}
              currentAgentId={currentAgentId}
              onAllTicketsClick={() => handleCategoryClick(null)}
              onExpand={() => setCollapsed(false)}
            />
          )}
        </div>
      </div>

      {/* Category Dialog */}
      <CategoryDialog
        open={showCategoryDialog}
        onOpenChange={setShowCategoryDialog}
        editingCategory={editingCategory}
        categoryName={categoryName}
        setCategoryName={setCategoryName}
        categoryColor={categoryColor}
        setCategoryColor={setCategoryColor}
        onSave={saveCategory}
        saving={savingCategory}
      />

      {/* Tag Dialog */}
      <TagDialog
        open={showTagDialog}
        onOpenChange={setShowTagDialog}
        editingTag={editingTag}
        tagName={tagName}
        setTagName={setTagName}
        tagColor={tagColor}
        setTagColor={setTagColor}
        onSave={saveTag}
        saving={savingTag}
      />

      {/* Delete Category Confirmation */}
      <DeleteCategoryDialog
        category={categoryToDelete}
        onOpenChange={setCategoryToDelete}
        onDelete={deleteCategory}
      />

      {/* Delete Tag Confirmation */}
      <DeleteTagDialog
        tag={tagToDelete}
        onOpenChange={setTagToDelete}
        onDelete={deleteTag}
      />
    </>
  );
}
